import {
  type ScheduleDefinitionFailure,
  type ScheduleInvalidDefinition,
  type ScheduleSaveDefinitionInput,
  ScheduledJobDefinition,
  type ScheduledJobDefinition as ScheduledJobDefinitionValue,
  type ScheduledJobDefinitionReadModel,
  type ScheduledJobId,
  type ScheduledJobRevision,
} from "@afar-research/contracts";
import * as Exit from "effect/Exit";
import * as Schema from "effect/Schema";
import * as SchemaGetter from "effect/SchemaGetter";

export const SCHEDULE_DEFINITIONS_DIRECTORY = ".afar-research/schedule";

export type ScheduledJobDefinitionParseResult =
  | { readonly ok: true; readonly definition: ScheduledJobDefinitionValue }
  | {
      readonly ok: false;
      /** The definition targets a removed Thread Workflow; readers drop it (ADR-0141). */
      readonly retired?: true;
      readonly issues: readonly string[];
    };

export type ScheduledJobDefinitionSaveDecision =
  | {
      readonly ok: true;
      readonly action: "write";
      readonly definition: ScheduledJobDefinitionValue;
    }
  | {
      readonly ok: true;
      readonly action: "already-applied";
      readonly saved: ScheduledJobDefinitionReadModel;
    }
  | {
      readonly ok: false;
      readonly reason: ScheduleDefinitionFailure;
      readonly message: string;
      readonly relativePath?: string;
      readonly expectedRevision?: ScheduledJobRevision;
      readonly observedRevision?: ScheduledJobRevision;
    };

const decodeScheduledJobDefinition = Schema.decodeUnknownExit(ScheduledJobDefinition);
const scheduledJobDefinitionEquivalence = Schema.toEquivalence(ScheduledJobDefinition);

const RETIRED_THREAD_WORKFLOW_TAGS = new Set([
  "ThreadWorkflow",
  "WorkflowTargetBlocked",
  "BlockedWorkflowTarget",
]);
const RETIRED_THREAD_WORKFLOW_KEYS = [
  "workflowConfiguration",
  "workflowLaunch",
  "workflowLaunchConfiguration",
  "workflowLaunchConfigurationRevision",
  "workflowActivation",
] as const;

/**
 * Thread Workflows were removed (ADR-0141). A stored schedule record that targets one, or carries
 * Thread Workflow host state, is dropped when read rather than surfaced as blocked or invalid.
 */
export function isRetiredThreadWorkflowScheduleRecord(input: unknown): boolean {
  if (Array.isArray(input)) return input.some(isRetiredThreadWorkflowScheduleRecord);
  if (typeof input !== "object" || input === null) return false;
  const record = input as Record<string, unknown>;
  if (typeof record._tag === "string" && RETIRED_THREAD_WORKFLOW_TAGS.has(record._tag)) {
    return true;
  }
  if (RETIRED_THREAD_WORKFLOW_KEYS.some((key) => record[key] !== undefined)) return true;
  return Object.values(record).some(isRetiredThreadWorkflowScheduleRecord);
}

export function migrateLegacySchedulePersistence(input: unknown): unknown {
  if (Array.isArray(input)) {
    return input
      .filter((entry) => !isRetiredThreadWorkflowScheduleRecord(entry))
      .map(migrateLegacySchedulePersistence);
  }
  if (typeof input !== "object" || input === null) return input;
  const migrated = Object.fromEntries(
    Object.entries(input).map(([key, value]) => [key, migrateLegacySchedulePersistence(value)]),
  );
  if (
    typeof migrated.jobId !== "string" ||
    typeof migrated.displayName !== "string" ||
    typeof migrated.taskPrompt !== "string" ||
    (migrated.provider !== "codex" && migrated.provider !== "claudeAgent") ||
    migrated.target !== undefined
  ) {
    return migrated;
  }
  const { taskPrompt, provider, ...definition } = migrated;
  return {
    ...definition,

    target: { _tag: "ProviderPrompt", taskPrompt, provider },
  };
}

/** Decode old persisted schedule ledgers without weakening the current public contract. */
export function withScheduledJobDefinitionPersistenceMigration<S extends Schema.Top>(schema: S) {
  return Schema.Unknown.pipe(
    Schema.decodeTo(schema, {
      decode: SchemaGetter.transform(migrateLegacySchedulePersistence),
      encode: SchemaGetter.transform((value) => value),
    }),
  );
}

function isSupportedIanaTimeZone(timeZone: string): boolean {
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone }).resolvedOptions().timeZone.length > 0;
  } catch {
    return false;
  }
}

export function parseScheduledJobDefinition(input: unknown): ScheduledJobDefinitionParseResult {
  if (isRetiredThreadWorkflowScheduleRecord(input)) {
    return {
      ok: false,
      retired: true,
      issues: ["Thread Workflow targets are no longer supported."],
    };
  }
  const decoded = decodeScheduledJobDefinition(migrateLegacySchedulePersistence(input));
  if (Exit.isFailure(decoded)) {
    return { ok: false, issues: ["The definition contains an unsupported field or value."] };
  }
  if (!isSupportedIanaTimeZone(decoded.value.timeZone)) {
    return { ok: false, issues: ["The timezone must be a supported IANA timezone."] };
  }
  return { ok: true, definition: decoded.value };
}

export function scheduledJobDefinitionRelativePath(jobId: ScheduledJobId): string {
  return `${SCHEDULE_DEFINITIONS_DIRECTORY}/${jobId}.json`;
}

export function classifyScheduledJobDefinitions(input: {
  readonly definitions: readonly ScheduledJobDefinitionReadModel[];
  readonly invalidDefinitions: readonly ScheduleInvalidDefinition[];
}): {
  readonly definitions: readonly ScheduledJobDefinitionReadModel[];
  readonly invalidDefinitions: readonly ScheduleInvalidDefinition[];
} {
  const pathsByJobId = new Map<string, string[]>();
  for (const entry of input.definitions) {
    const paths = pathsByJobId.get(entry.definition.jobId) ?? [];
    paths.push(entry.relativePath);
    pathsByJobId.set(entry.definition.jobId, paths);
  }
  const duplicatePaths = new Set(
    [...pathsByJobId.values()].filter((paths) => paths.length > 1).flat(),
  );
  const duplicateDefinitions = input.definitions
    .filter((entry) => duplicatePaths.has(entry.relativePath))
    .map((entry) => ({
      relativePath: entry.relativePath,
      detail: `Stable job identity '${entry.definition.jobId}' appears in multiple files.`,
    }));
  return {
    definitions: input.definitions
      .filter((entry) => !duplicatePaths.has(entry.relativePath))
      .toSorted((left, right) => left.relativePath.localeCompare(right.relativePath)),
    invalidDefinitions: [...input.invalidDefinitions, ...duplicateDefinitions].toSorted(
      (left, right) => left.relativePath.localeCompare(right.relativePath),
    ),
  };
}

export function decideScheduledJobDefinitionSave(input: {
  readonly request: ScheduleSaveDefinitionInput;
  readonly definitions: readonly ScheduledJobDefinitionReadModel[];
  readonly targetRelativePath: string;
  readonly targetExists: boolean;
}): ScheduledJobDefinitionSaveDecision {
  const parsed = parseScheduledJobDefinition(input.request.definition);
  if (!parsed.ok) {
    return {
      ok: false,
      reason: "definition-invalid",
      message: parsed.issues.join(" "),
    };
  }
  const existingRelativePath = input.request.existingRelativePath;
  if (existingRelativePath === null) {
    const duplicate = input.definitions.find(
      (entry) => entry.definition.jobId === parsed.definition.jobId,
    );
    if (
      duplicate &&
      duplicate.relativePath === input.targetRelativePath &&
      scheduledJobDefinitionEquivalence(duplicate.definition, parsed.definition)
    ) {
      return { ok: true, action: "already-applied", saved: duplicate };
    }
    if (duplicate) {
      return {
        ok: false,
        reason: "duplicate-job-id",
        message: `Stable job identity '${parsed.definition.jobId}' already exists.`,
        relativePath: duplicate.relativePath,
      };
    }
    if (input.targetExists) {
      return {
        ok: false,
        reason: "duplicate-job-id",
        message: `Definition target '${input.targetRelativePath}' already exists.`,
        relativePath: input.targetRelativePath,
      };
    }
    return { ok: true, action: "write", definition: parsed.definition };
  }

  const existing = input.definitions.find((entry) => entry.relativePath === existingRelativePath);
  if (!existing) {
    return {
      ok: false,
      reason: "definition-not-found",
      message: `Scheduled Job Definition '${existingRelativePath}' was not found.`,
      relativePath: existingRelativePath,
    };
  }
  if (existing.definition.jobId !== parsed.definition.jobId) {
    return {
      ok: false,
      reason: "identity-conflict",
      message: "Stable job identity cannot be changed while editing a definition.",
      relativePath: existingRelativePath,
    };
  }
  if (
    input.request.expectedRevision != null &&
    existing.revision !== input.request.expectedRevision
  ) {
    if (scheduledJobDefinitionEquivalence(existing.definition, parsed.definition)) {
      return { ok: true, action: "already-applied", saved: existing };
    }
    return {
      ok: false,
      reason: "revision-conflict",
      message: "The Scheduled Job Definition changed in another client. Reload before saving.",
      relativePath: existingRelativePath,
      ...(input.request.expectedRevision
        ? { expectedRevision: input.request.expectedRevision }
        : {}),
      observedRevision: existing.revision,
    };
  }
  return { ok: true, action: "write", definition: parsed.definition };
}
